import React, { useState } from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import {
  useDeviceStatus,
  useUpdateDeviceModes,
} from "hooks/useDevices";
import { useQueryClient } from "@tanstack/react-query";
import { notify } from "utils/notify";
import { DeviceRegion } from "types/device.types";
import styled from "styled-components";
import { theme } from "styles/theme";

import { StatusParametersTable } from "./device/StatusParametersTable";
import OperationModesPanel from "./device/OperationModesPanel";
import { ScheduleSection } from "./device/status/ScheduleSection";
import { useDeviceStatusParams } from "./device/status/useDeviceStatusParams";
import { PageError } from "./shared/PageError";

import {
  LoadingContainer,
  StatusLayout,
} from "styles/devices/deviceDetails.styles";

const StatusContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DisclaimerContainer = styled.div`
  background-color: ${theme.colors.bgLight};
  color: ${theme.colors.textSecondary};
  padding: 12px 16px;
  border-radius: ${theme.borderRadius.small};
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.85rem;
  border: 1px solid ${theme.colors.border};
`;

interface DeviceStatusProps {
  serialNumber: string;
  region: DeviceRegion;
}

export const DeviceStatus: React.FC<DeviceStatusProps> = ({
  serialNumber,
  region,
}) => {
  const { data, isLoading, error, refetch, isRefetching } =
    useDeviceStatus(serialNumber);
  const updateDeviceModes = useUpdateDeviceModes();
  const [updatingMode, setUpdatingMode] = useState(false);
  const queryClient = useQueryClient();

  const params = useDeviceStatusParams({
    serialNumber,
    shadow: data?.response?.shadow,
    refetch,
  });

  const handleSaveModes = (modes: string[]) => {
    setUpdatingMode(true);

    updateDeviceModes.mutate(
      { serialNumber, modes },
      {
        onSuccess: (response) => {
          if (response.success && response.device) {
            queryClient.setQueryData(
              ["deviceStatus", serialNumber],
              (oldData: any) => {
                if (!oldData) return oldData;
                const newData = JSON.parse(JSON.stringify(oldData));
                newData.response.shadow.modes = response.device?.modes || [];
                return newData;
              },
            );
            notify.success("Modes updated successfully");
          } else {
            notify.warning("Update succeeded but returned unexpected data");
          }
        },
        onError: (err: any) => {
          console.error("Failed to update modes:", err);
          notify.error(
            err?.response?.data?.message || "Failed to update modes",
          );
        },
        onSettled: () => {
          setUpdatingMode(false);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <ProgressSpinner />
      </LoadingContainer>
    );
  }

  if (error || !data || !data.success) {
    const status = (error as any)?.response?.status;
    const apiMessage = (error as any)?.response?.data?.message;
    const detail =
      apiMessage ||
      (error instanceof Error ? error.message : "Unknown error") ||
      "Unknown error";

    return (
      <PageError
        title="Error loading device status"
        message={`${status ? `(${status}) ` : ""}${detail}`}
        onRetry={() => refetch()}
      />
    );
  }

  const { shadow } = data.response;
  const schedules = shadow.schedules || [];

  if (shadow.flow === undefined) {
    shadow.flow = 0;
  }

  return (
    <>
      <StatusContainer>
        <StatusLayout>
          <StatusParametersTable
            shadow={shadow}
            isEditingParams={params.isEditingParams}
            updatingParams={params.updatingParams}
            pendingParams={params.pendingParams}
            toggleParamEditMode={params.toggleParamEditMode}
            handleSaveParams={params.handleSaveParams}
            handleParamChange={params.handleParamChange}
            handleToggleSensorConnection={params.handleToggleSensorConnection}
            handleEditParameter={params.handleEditParameter}
            editingParameter={params.editingParameter}
            handleCancelEdit={params.handleCancelEdit}
            hasDirtyParams={params.hasDirtyParams}
            dirtyCount={params.dirtyCount}
            onRefresh={() => refetch()}
            isRefreshing={isRefetching}
          />

          <div>
            <OperationModesPanel
              deviceData={data}
              updatingMode={updatingMode}
              handleSaveModes={handleSaveModes}
              region={region}
            />

            {region === DeviceRegion.AU && (
              <ScheduleSection
                serialNumber={serialNumber}
                schedules={schedules}
                firmwareVersion={shadow.firmwareVersion || "1.0.0"}
              />
            )}
          </div>
        </StatusLayout>
      </StatusContainer>

      <DisclaimerContainer>
        NOTE: The values in this page may take up to 30s to update
      </DisclaimerContainer>
    </>
  );
};
